import React, { createContext, useMemo, useContext } from 'react';
import { useIntl } from 'react-intl';
import get from 'lodash/get';
import merge from 'lodash/merge';
import PropTypes from 'prop-types';
import { PortalConfigContext} from '../../../config/portal';
import { getPortalName, getCurrentLocale } from '../../../utils/language';
import { getCustomUom } from '../../../utils/uomHelper';
import { adsConfig } from './resources/adsConfig';
import { getImmersiveTour } from '../../../utils/immersiveTourHelper';
import { useCookies } from 'react-cookie';
import { getBoatUrl } from '../../../utils/urlHelpers/boat';
import { showMoreProducts, getAddress } from '../../../utils/commonHelper';
import {
  addResizeParams,
  get360Photos,
  getMediaImages,
  getMediaVideos,
  getTitle,
  getPrice,
  getLocation,
  isFSBOContact,
  showPriceCalculation,
  getMediaVideoUrl
} from '../../../utils/listingHelper';
import {asArray} from '../../../utils';
import { getMessages } from '../../../tppServices/translations/messages';

export const BDPContext = createContext();
const BDPContextProvider = BDPContext.Provider;

const mediaImagesFormatter = (resize, fullListingName) => ({ title, original, url, date }) => {
  const src = addResizeParams(
    original ? original.url : url,
    {...resize, ratio: true},
    date.modified,
    true,
    'webp'
  );
  const srcset = `
    ${addResizeParams(
      original ? original.url : url,
      { width: 300, height: 222, ratio: true },
      date.modified,
      true,
      'webp'
    )} 300w,
    ${addResizeParams(
      original ? original.url : url,
      { width: 402, height: 267, ratio: true },
      date.modified,
      true,
      'webp'
    )} 402w,
    ${addResizeParams(
      original ? original.url : url,
      { width: 702, height: 466, ratio: true },
      date.modified,
      true,
      'webp'
    )} 702w,
    ${addResizeParams(
      original ? original.url : url,
      { width: 969, height: 646, ratio: true },
      date.modified,
      true,
      'webp'
    )} 969w,
    ${addResizeParams(
      original ? original.url : url,
      { width: 801, height: 533, ratio: true },
      date.modified,
      true,
      'webp'
    )} 1200w,
    ${addResizeParams(
      original ? original.url : url,
      { width: 1028, height: 685, ratio: true },
      date.modified,
      true,
      'webp'
    )} 1600w
  `;
  const sizes = `
    (max-width: 300px) 300px,
    (max-width: 402px) 402px,
    (max-width: 702px) 702px,
    (max-width: 969px) 969px,
    (max-width: 1200px) 1040px,
    1600px
  `;
  return {
    alt: title || fullListingName,
    src,
    srcset,
    sizes
  };
};

const videoFormatter = (mediaVideo, title) => {
  return {
    title: title,
    src: getMediaVideoUrl(mediaVideo),
    mediaType: mediaVideo.mediaType,
    overlay: true,
    poster: mediaVideo.videoVideoThumbnailUri,
    thumbnailVideo: !mediaVideo.videoVideoThumbnailUri
  };
};

const image360Formatter = (image360, title, i) => {
  // replace all blank space from title
  title = title.replace(/\s/g, '-');
  const timestamp = Math.floor(Date.now() / 1000);
  return {
    id: `360-${title}-${i}`,
    src: image360.url?.replace('LARGE', 'XLARGE') + `?w=1028&h=685&t=${timestamp}`,
    mediaType: '360Image'
  };
};

const orderFormattedMediaItems = (formattedMediaImages, formattedMediaVideos, formatted360) => {
  const [head, ...tail] = formattedMediaImages;
  let firstImage = asArray(head);
  let videos = asArray(formattedMediaVideos);
  let images360 = asArray(formatted360);
  const items = [...firstImage, ...videos, ...images360, ...tail];
  return items;
};

const formatMediaItems = (
  mediaImages,
  mediaVideos,
  images360,
  fullListingName,
  resize
) => {
  const imageFormatter = mediaImagesFormatter(resize, fullListingName);
  const videoTitle = mediaVideos?.title || fullListingName;
  const formattedMediaImages = mediaImages.map(imageFormatter);
  const formattedMediaVideos = mediaVideos.map((mediaVideo) => videoFormatter(mediaVideo, videoTitle));
  const formattedMedia360 = images360.map((img360, i) => image360Formatter(img360, fullListingName, i));
  const items = orderFormattedMediaItems(formattedMediaImages, formattedMediaVideos, formattedMedia360);
  return { formattedMediaImages, formattedMediaVideos, mediaItems: items };
};

const parseListing = (listing) => {
  const url = getBoatUrl(listing);
  const mediaImages = getMediaImages(listing);
  const mediaVideos = getMediaVideos(listing);
  const images360 = get360Photos(listing);
  const listingTitle = getTitle(listing);
  const virtualTourUrl = get(listing, 'virtualTour.0.url');
  const oemDetails = listing.oemDetails;
  return {
    url,
    mediaImages,
    mediaVideos,
    images360,
    listingTitle,
    virtualTourUrl,
    oemDetails
  };
};

const parseBdpProps = (props, context, intl, cookies) => {
  const { listing } = props;
  const parsedListing = parseListing(listing);
  const resize = props.resize || { width: 200, height: 146 };
  const { mediaImages, mediaVideos, images360, listingTitle } = parsedListing;
  const t = intl.formatMessage;
  const carouselItems = formatMediaItems(
    mediaImages,
    mediaVideos,
    images360,
    listingTitle,
    resize
  );
  const uom = get(context, 'uom.length.abbr');
  const seoConfig = get(context, 'pages.details.seo');
  const customMeta = get(context, 'supports.customMeta', false);
  const showThirdAdbox = get(context, 'supports.showThirdAdBox', false);
  const portal = getPortalName(context);
  const revealDealerPhoneNumber = get(
    context,
    `languages.${intl.locale}.revealDealerPhoneNumber`,
    false
  );
  const enableDealerLocatorV2 = get(
    context,
    'supports.enableDealerLocatorV2',
    false
  );
  const showBottomAdBox = get(context, 'supports.showBottomAdBox', false);
  const supportsSavedSearchOnBDP = get(
    context,
    'supports.savedSearchOnBDP',
    false
  );

  const getCustomUom2 = () => {
    const isDefaultLocaleUom = get(context, 'supports.defaultUoms', false);
    const supportedUoms = get(context, 'supportedUoms');
    const defaultLocaleUom = get(
      context,
      `languages.${intl.locale}.defaultUom`
    );
    return getCustomUom(
      isDefaultLocaleUom,
      cookies,
      supportedUoms,
      defaultLocaleUom
    );
  };

  const getPageAdsConfig = () => {
    const portalAdsConfig = get(context, 'pages.details.adsConfig', {});
    const selectedAds = Object.keys(portalAdsConfig).reduce(
      (resultAds, adsKey) => {
        resultAds[adsKey] = adsConfig[adsKey];
        return resultAds;
      },
      {}
    );
    return merge(portalAdsConfig, selectedAds);
  };

  const hasImmersiveTour = get(context, 'supports.immersiveTour', false);
  const showImmersiveTour = hasImmersiveTour && !!getImmersiveTour(listing);

  const locale = getCurrentLocale();

  const supportsTridentCalculator = context?.languages?.[locale]?.supports?.tridentCalculator;

  const supportBoatLoansSection = context?.languages?.[locale]?.supports?.bdpBoatLoansSection;
  const showCalculator = showPriceCalculation(listing, context);

  const currentLocale = intl.locale;

  const currencyCode = context?.languages?.[currentLocale]?.currency?.abbr;

  const customPricingOptions = {
    currency: context?.supportedCurrencies?.[currencyCode],
    formatingOptions: context?.languages?.[currentLocale]?.currency?.format
   };

  const showMoreInventory = showMoreProducts(context?.supports?.moreProductsButton);

  const supportsMonthlyPayment = context?.languages?.[locale]?.supports?.monthlyPayment;

  const supportsConversationalCta = context?.supports?.bdp?.conversationalCta;
  const manufacturerListings = context?.supports?.manufacturerListings;

  const servicesItems = context?.services?.itemsByDFP || [];

  const messages = getMessages();
  const buttonThumbnail = {
    buttonText: `${t(messages.viewCarouselPhotos.view)} ${mediaImages.length} ${
      mediaImages.length > 1 ? t(messages.viewCarouselPhotos.photos) : t(messages.viewCarouselPhotos.photo)
    }`,
    src: carouselItems.formattedMediaImages[0]?.src,
    srcset: carouselItems.formattedMediaImages[0]?.srcset,
    sizes: carouselItems.formattedMediaImages[0]?.sizes,
    show: mediaImages.length > 0,
    alt: carouselItems.formattedMediaImages[0]?.alt
  };

  const carouselThumbnails = [
    buttonThumbnail,
    ...carouselItems.mediaItems
  ];

  const summaryCtaProps = {
    title: getTitle(listing),
    price: getPrice(listing, customPricingOptions),
    location: getLocation(listing),
    classNames: {
      boatInfoModal: !isFSBOContact(listing.contact)
    },
    phoneNumber: listing.contact?.phone,
    phoneText: t(messages.contactGroup?.phoneReveal)
  };

  const address = listing.contact?.address || {};
  const addressUrl = `https://maps.google.com/?q=${encodeURIComponent(
    getAddress(address)
  )}`;
  const sellerNameProps = {
    displayPhoneMessage: true,
    hidePhone: isFSBOContact(listing.contact),
    phoneNumber: listing.contact?.phone,
    phoneText: t(messages.contactGroup?.phoneReveal),
    linkContactInfo: true,
    addressLink: addressUrl,
    address: getAddress(address),
  };

  return {
    listing,
    uom,
    seoConfig,
    customMeta,
    customPricingOptions,
    showThirdAdbox,
    portal,
    revealDealerPhoneNumber,
    enableDealerLocatorV2,
    showBottomAdBox,
    supportsSavedSearchOnBDP,
    showImmersiveTour,
    customUom: getCustomUom2(),
    pageAdsConfig: getPageAdsConfig(),
    showCalculator,
    showManufacturerForm: false,
    showMoreInventory,
    servicesItems,
    supportsTridentCalculator,
    supportBoatLoansSection,
    supportsMonthlyPayment,
    supportsConversationalCta,
    manufacturerListings,
    carouselItems,
    carouselThumbnails,
    summaryCtaProps,
    sellerNameProps,
    ...parsedListing
  };
};

const BoatDetailsPage = ({ children, ...props }) => {
  const intl = useIntl();
  const [cookies] = useCookies();

  const portalConfigContext = useContext(PortalConfigContext);
  const bdpContext = useMemo(
    () => parseBdpProps(props, portalConfigContext, intl, cookies),
    [props]
  );

  return <BDPContextProvider value={bdpContext}>{children}</BDPContextProvider>;
};

BoatDetailsPage.propTypes = {
  children: PropTypes.any
};

export default BoatDetailsPage;
